/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import './AdminDashboard.css';

// API Base URL - for production deployment
// Fallback to Render backend if env variable not set at build time
const API_BASE = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com';

// Indexing routes now require the admin token (security audit Oct 2026, C1).
const adminAuthHeader = () => {
  const token = localStorage.getItem('adminAccessToken');
  const twoFA = localStorage.getItem('admin2FASession');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // Needed when the admin has 2FA on (adminApi.js sends it for axios calls)
    ...(twoFA ? { 'x-2fa-session': twoFA } : {}),
  };
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, session, logout, sessionExpiryWarning } = useAdminAuth();

  // Existing State
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHealthModal, setShowHealthModal] = useState(false);

  // Jev outcome visibility (2026-10-11). In-process UTC-day counters
  // served by GET /api/admin/jev/stats. Reads only; no mutation.
  const [jevStats, setJevStats] = useState(null);

  // Vector Indexing State
  const [indexingStatus, setIndexingStatus] = useState({
    total: 0,
    verticals: {},
    needsIndexing: true,
    loading: true
  });
  const [indexingJob, setIndexingJob] = useState(null);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [fixResult, setFixResult] = useState(null);
  const [indexingAction, setIndexingAction] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // Guided workflow: 1=Diagnose, 2=Fix, 3=Index

  /**
   * Load dashboard data
   */
  useEffect(() => {
    loadDashboardData();
    fetchIndexingStatus();
    fetchJevStats();

    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData();
      fetchIndexingStatus();
      fetchJevStats();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchJevStats = async () => {
    try {
      const res = await adminApi.get('/api/admin/jev/stats');
      // adminApi returns the parsed JSON body OR the axios-style
      // { data } envelope depending on the client version. Handle both.
      const payload = res && res.data ? res.data : res;
      if (payload && payload.success && payload.data) {
        setJevStats(payload.data);
      }
    } catch (e) {
      // Never surface a tile fetch error to the user -- the rest of
      // the dashboard must stay usable even if the counters module is
      // temporarily unreachable.
      /* eslint-disable no-console */
      console.warn('[AdminDashboard] fetchJevStats:', e && e.message);
      /* eslint-enable no-console */
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all data in parallel
      // adminApi functions already return response.data
      // So jobsData/adaptersData are directly: { success, count, jobs/adapters }
      const [jobsData, adaptersData, healthData] = await Promise.all([
        adminApi.getTrainingJobs({ limit: 5 }).catch(err => {
          console.error('[Dashboard] Jobs API failed:', err);
          return { jobs: [], count: 0 };  // No extra .data wrapper needed
        }),
        adminApi.getAdapters().catch(err => {
          console.error('[Dashboard] Adapters API failed:', err);
          return { adapters: [], count: 0 };  // No extra .data wrapper needed
        }),
        adminApi.getSystemHealth().catch(err => {
          console.error('[Dashboard] Health API failed:', err);
          return { status: 'unknown' };  // No extra .data wrapper needed
        })
      ]);

      console.log('[Dashboard] jobsData:', JSON.stringify(jobsData, null, 2));
      console.log('[Dashboard] adaptersData:', JSON.stringify(adaptersData, null, 2));

      // Set recent jobs
      setRecentJobs(jobsData.jobs || []);

      // Set adapters
      setAdapters(adaptersData.adapters || []);

      // Set system health
      setSystemHealth(healthData);

      // Calculate stats
      const totalJobs = jobsData.count || 0;
      const activeAdapters = (adaptersData.adapters || []).filter(a => a.active).length;

      setStats({
        totalJobs,
        activeAdapters,
        recentSubmissions: (jobsData.jobs || []).length
      });

    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard data. Check backend logs.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // VECTOR INDEXING FUNCTIONS
  // ============================================

  const fetchIndexingStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/indexing/status`, { headers: adminAuthHeader() });
      if (!res.ok) throw new Error('Status API failed');
      const data = await res.json();
      if (data.success) {
        setIndexingStatus({ ...data, loading: false });
        setIndexingJob(data.currentJob);
        // Auto-determine step based on status
        if (data.total >= 500) {
          setCurrentStep(3);
        }
      }
    } catch (err) {
      console.error('Failed to fetch indexing status:', err);
      setIndexingStatus(prev => ({ ...prev, loading: false, error: err.message }));
    }
  };

  const runDiagnosis = async () => {
    setIndexingAction('diagnose');
    setDiagnosisResult(null);
    setFixResult(null);
    try {
      const res = await fetch(`${API_BASE}/admin/indexing/diagnose`, { method: 'POST', headers: adminAuthHeader() });
      if (!res.ok) throw new Error('Diagnose API failed');
      const data = await res.json();
      if (data.success) {
        setDiagnosisResult(data.diagnosis);
        // Move to next step based on diagnosis
        if (data.diagnosis.summary.brokenFiles === 0) {
          setCurrentStep(3); // Skip fix, go to index
        } else {
          setCurrentStep(2); // Need to fix
        }
      }
    } catch (err) {
      console.error('Diagnosis failed:', err);
      setDiagnosisResult({ error: err.message });
    } finally {
      setIndexingAction(null);
    }
  };

  const runFixJSON = async () => {
    setIndexingAction('fix');
    setFixResult(null);
    try {
      const res = await fetch(`${API_BASE}/admin/indexing/fix-json`, { method: 'POST', headers: adminAuthHeader() });
      if (!res.ok) throw new Error('Fix API failed');
      const data = await res.json();
      if (data.success) {
        setFixResult(data);
        // Re-run diagnosis to verify fixes
        setTimeout(() => runDiagnosis(), 1000);
      }
    } catch (err) {
      console.error('Fix failed:', err);
      setFixResult({ error: err.message });
    } finally {
      setIndexingAction(null);
    }
  };

  const startIndexing = async (forceReindex = false) => {
    setIndexingAction('index');
    try {
      const res = await fetch(`${API_BASE}/admin/indexing/index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeader() },
        body: JSON.stringify({ forceReindex })
      });
      if (!res.ok) throw new Error('Index API failed');
      const data = await res.json();
      if (data.success) {
        setIndexingJob(data.job);
        pollJobStatus();
      }
    } catch (err) {
      console.error('Indexing failed:', err);
      setIndexingJob({ status: 'failed', error: err.message });
    } finally {
      setIndexingAction(null);
    }
  };

  const pollJobStatus = () => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/indexing/job`, { headers: adminAuthHeader() });
        const data = await res.json();
        setIndexingJob(data.job);

        if (!data.job || data.job.status !== 'running') {
          clearInterval(interval);
          fetchIndexingStatus();
        }
      } catch (err) {
        clearInterval(interval);
      }
    }, 3000);
  };

  const clearAllIndexing = async () => {
    if (!window.confirm('Clear ALL indexed data? Chat will not work until re-indexed.')) return;

    setIndexingAction('clear');
    try {
      await fetch(`${API_BASE}/admin/indexing/clear`, { method: 'DELETE', headers: adminAuthHeader() });
      await fetchIndexingStatus();
      setCurrentStep(1);
      setDiagnosisResult(null);
      setFixResult(null);
    } catch (err) {
      console.error('Clear failed:', err);
    } finally {
      setIndexingAction(null);
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  /**
   * Format time remaining
   */
  const formatTimeRemaining = () => {
    if (!session || !session.minutesUntilExpiry) return '--';

    const minutes = session.minutesUntilExpiry;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  /**
   * Get status badge class
   */
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'status-success';
      case 'running':
      case 'in_progress':
        return 'status-running';
      case 'failed':
      case 'error':
        return 'status-failed';
      case 'pending':
      case 'queued':
        return 'status-pending';
      default:
        return 'status-unknown';
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format duration between start and end dates
   */
  const formatDuration = (startDate, endDate, status) => {
    if (!startDate) return '--';

    const start = new Date(startDate);
    let end;

    // For completed jobs, use the completion time
    if (status === 'completed' && endDate) {
      end = new Date(endDate);
    }
    // For in-progress jobs, calculate duration up to now
    else if (['training', 'uploading_dataset', 'submitting_training', 'preparing_data', 'in_progress'].includes(status)) {
      end = new Date();
    }
    // For failed/pending jobs without end time
    else if (status === 'failed' && endDate) {
      end = new Date(endDate);
    }
    else {
      return '--';
    }

    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    if (diffHours > 0) {
      return `${diffHours}h ${remainingMins}m`;
    } else if (diffMins > 0) {
      return `${diffMins}m`;
    } else {
      return '< 1m';
    }
  };

  if (loading && !stats) {
    return (
      <div className="admin-dashboard loading">
        <div className="spinner-large"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-dashboard">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <h1>Admin Console</h1>
            <p className="header-subtitle">SkillDelivery Platform Management</p>
          </div>

          <div className="header-right">
            {/* Session Warning */}
            {sessionExpiryWarning && (
              <div className="session-warning">
                ⚠️ Session expiring soon: {formatTimeRemaining()}
              </div>
            )}

            {/* User Info */}
            <div className="user-info">
              <div className="user-avatar">
                {user?.fullName?.charAt(0) || 'A'}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.fullName || 'Admin'}</div>
                <div className="user-role">{user?.role || 'ADMIN'}</div>
              </div>
            </div>

            {/* Session Timer */}
            <div className="session-timer">
              <span className="timer-icon">⏱️</span>
              <span className="timer-text">{formatTimeRemaining()}</span>
            </div>

            {/* Logout Button */}
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        {/* Vector Indexing Alert - Shows when DB needs indexing */}
        {!indexingStatus.loading && indexingStatus.needsIndexing && (
          <div className="indexing-alert warning">
            <div className="indexing-alert-content">
              <span className="indexing-alert-icon">⚠️</span>
              <div className="indexing-alert-text">
                <strong>Vector Database Needs Indexing</strong>
                <span>Chat responses won't work correctly. Current: <strong>{indexingStatus.total} chunks</strong> (need 500+)</span>
              </div>
            </div>
            <button
              className="indexing-alert-action"
              onClick={() => document.getElementById('indexing-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Setup Now ↓
            </button>
          </div>
        )}

        {/* Indexing In Progress Alert */}
        {indexingJob?.status === 'running' && (
          <div className="indexing-alert info">
            <div className="indexing-alert-content">
              <span className="indexing-alert-icon spinning">🔄</span>
              <div className="indexing-alert-text">
                <strong>Indexing in Progress...</strong>
                <span>{indexingJob.progress || 'Processing training files across all verticals'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Main Content */}
        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card clickable" onClick={() => navigate('/admin/training/jobs')}>
              <div className="stat-icon stat-icon-jobs">📊</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalJobs || 0}</div>
                <div className="stat-label">Training Jobs</div>
              </div>
            </div>

            <div className="stat-card clickable" onClick={() => navigate('/admin/training/adapters')}>
              <div className="stat-icon stat-icon-adapters">🔌</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.activeAdapters || 0}</div>
                <div className="stat-label">Active Adapters</div>
              </div>
            </div>

            <div className="stat-card clickable" onClick={() => navigate('/admin/training/jobs')}>
              <div className="stat-icon stat-icon-recent">📝</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.recentSubmissions || 0}</div>
                <div className="stat-label">Recent Submissions</div>
              </div>
            </div>

            <div className="stat-card clickable" onClick={() => setShowHealthModal(true)} title="Click to view system health details">
              <div className="stat-icon stat-icon-health">💙</div>
              <div className="stat-info">
                <div className="stat-value">
                  {systemHealth?.status === 'healthy' ? '✓' : '✗'}
                </div>
                <div className="stat-label">System Health</div>
              </div>
            </div>
          </div>

          {/* Jev outcome visibility (2026-10-11) --
              today's calls per caller, success %, top-3 reasons.
              Flagged by server-side JEV_ALERTS_ENABLED (default off);
              counters themselves always run. */}
          {jevStats && (
            <div className="jev-health-row"
                 style={{ display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 16,
                          marginBottom: 24 }}>
              {['score', 'decide'].map((caller) => {
                const b = jevStats[caller] || {};
                const pct = b.success_pct == null ? '—' : `${b.success_pct}%`;
                const belowThreshold = b.total >= (jevStats.thresholds && jevStats.thresholds.min_calls || 20)
                  && b.success_pct != null
                  && b.success_pct < (jevStats.thresholds && jevStats.thresholds.min_success_pct || 80);
                const pctColour = belowThreshold ? '#dc2626' : (b.success_pct >= 95 ? '#16a34a' : '#64748b');
                return (
                  <div key={caller}
                       style={{ background: 'white',
                                borderRadius: 8,
                                padding: 16,
                                boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
                                borderLeft: belowThreshold ? '4px solid #dc2626' : '4px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <strong>Jev / {caller}</strong>
                      <span style={{ color: '#64748b', fontSize: 12 }}>{b.day || jevStats.day} UTC</span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: pctColour, marginTop: 6 }}>
                      {pct}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 13 }}>
                      {b.ok || 0}/{b.total || 0} calls OK ({b.fail || 0} fail)
                    </div>
                    {b.top_reasons && b.top_reasons.length > 0 && (
                      <div style={{ marginTop: 10, fontSize: 12, color: '#334155' }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Top failures:</div>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {b.top_reasons.map((r) => (
                            <li key={r.reason}><code>{r.reason}</code> &middot; {r.count}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!jevStats.alerts_enabled && (
                      <div style={{ marginTop: 8, color: '#92400e', fontSize: 11 }}>
                        Alerts off (set JEV_ALERTS_ENABLED=true)
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Vector Database Indexing Section */}
          <div id="indexing-section" className="vector-indexing-section">
            <div className="section-header">
              <h2>🗄️ Vector Database Indexing</h2>
              <span className="section-subtitle">Index training data for SkillBot semantic search</span>
            </div>

            {/* Status Grid */}
            <div className="indexing-stats-grid">
              <div className={`indexing-stat total ${indexingStatus.total >= 500 ? 'success' : ''}`}>
                <div className="indexing-stat-value">
                  {indexingStatus.loading ? '...' : indexingStatus.total}
                </div>
                <div className="indexing-stat-label">Total Chunks</div>
                {indexingStatus.total >= 500 && <div className="indexing-stat-badge">✓ Ready</div>}
              </div>
              {['WMS', 'iSeries', 'Civil', 'Stitching', 'Platform'].map(vertical => (
                <div key={vertical} className="indexing-stat">
                  <div className="indexing-stat-value">
                    {indexingStatus.loading ? '...' : (indexingStatus.verticals[vertical]?.chunks || 0)}
                  </div>
                  <div className="indexing-stat-label">{vertical}</div>
                  <div className="indexing-stat-files">
                    {indexingStatus.verticals[vertical]?.files || 0} files
                  </div>
                </div>
              ))}
            </div>

            {/* Guided Workflow Steps */}
            <div className="indexing-workflow">
              <div className="workflow-title">Setup Workflow</div>
              <div className="workflow-steps">
                {/* Step 1: Diagnose */}
                <div className={`workflow-step ${currentStep >= 1 ? 'active' : ''} ${diagnosisResult && !diagnosisResult.error ? 'completed' : ''}`}>
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">Diagnose</div>
                    <div className="step-desc">Check JSON files & database</div>
                    <button
                      className="step-btn"
                      onClick={runDiagnosis}
                      disabled={indexingAction === 'diagnose'}
                    >
                      {indexingAction === 'diagnose' ? '🔄 Checking...' : '🔍 Run Diagnosis'}
                    </button>
                  </div>
                </div>

                <div className="workflow-arrow">→</div>

                {/* Step 2: Fix JSON */}
                <div className={`workflow-step ${currentStep >= 2 ? 'active' : ''} ${fixResult && !fixResult.error ? 'completed' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">Fix JSON</div>
                    <div className="step-desc">Auto-repair broken files</div>
                    <button
                      className="step-btn"
                      onClick={runFixJSON}
                      disabled={indexingAction === 'fix' || currentStep < 2}
                    >
                      {indexingAction === 'fix' ? '🔄 Fixing...' : '🔧 Fix Issues'}
                    </button>
                  </div>
                </div>

                <div className="workflow-arrow">→</div>

                {/* Step 3: Index */}
                <div className={`workflow-step ${currentStep >= 3 ? 'active' : ''} ${indexingStatus.total >= 500 ? 'completed' : ''}`}>
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">Index Data</div>
                    <div className="step-desc">Upload to vector database</div>
                    <button
                      className="step-btn primary"
                      onClick={() => startIndexing(false)}
                      disabled={indexingAction === 'index' || indexingJob?.status === 'running' || currentStep < 3}
                    >
                      {(indexingAction === 'index' || indexingJob?.status === 'running') 
                        ? '🔄 Indexing...' 
                        : '📥 Index All Verticals'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Secondary Actions */}
              <div className="workflow-secondary">
                <button
                  className="secondary-btn"
                  onClick={() => startIndexing(true)}
                  disabled={indexingAction || indexingJob?.status === 'running'}
                >
                  🔄 Force Re-Index
                </button>
                <button
                  className="secondary-btn danger"
                  onClick={clearAllIndexing}
                  disabled={indexingAction}
                >
                  🗑️ Clear All Data
                </button>
              </div>
            </div>

            {/* Job Result */}
            {indexingJob?.status === 'completed' && indexingJob.result && (
              <div className="indexing-result success">
                <strong>✅ Indexing Completed</strong>
                <span>
                  Indexed <strong>{indexingJob.result.totalIndexed}</strong> chunks in {indexingJob.result.elapsed}s
                  {indexingJob.result.totalErrors > 0 && (
                    <span className="error-count"> ({indexingJob.result.totalErrors} errors)</span>
                  )}
                </span>
              </div>
            )}

            {indexingJob?.status === 'failed' && (
              <div className="indexing-result error">
                <strong>❌ Indexing Failed:</strong> {indexingJob.error}
              </div>
            )}

            {/* Fix Result */}
            {fixResult && !fixResult.error && (
              <div className="indexing-result warning">
                <strong>🔧 JSON Fix Result:</strong> {fixResult.message}
                {fixResult.nextStep && (
                  <span className="next-step"> → {fixResult.nextStep}</span>
                )}
              </div>
            )}

            {/* Diagnosis Error */}
            {diagnosisResult?.error && (
              <div className="indexing-result error">
                <strong>❌ Diagnosis Failed:</strong> {diagnosisResult.error}
                <br />
                <small>Ensure backend route is configured: /api/admin/indexing/diagnose</small>
              </div>
            )}

            {/* Diagnosis Result */}
            {diagnosisResult && !diagnosisResult.error && (
              <div className="diagnosis-panel">
                <div className="diagnosis-header">
                  <strong>🔍 Diagnosis Report</strong>
                  <span className={`db-status ${diagnosisResult.database?.connected ? 'connected' : 'disconnected'}`}>
                    DB {diagnosisResult.database?.connected ? 'Connected' : 'Failed'}
                  </span>
                </div>

                <div className="diagnosis-grid">
                  <div className="diagnosis-section">
                    <div className="diagnosis-title">Summary</div>
                    <div className="diagnosis-items">
                      <div>Total Files: <strong>{diagnosisResult.summary?.totalFiles || 0}</strong></div>
                      <div className="text-success">Valid: <strong>{diagnosisResult.summary?.validFiles || 0}</strong></div>
                      {diagnosisResult.summary?.brokenFiles > 0 && (
                        <div className="text-error">Broken: <strong>{diagnosisResult.summary.brokenFiles}</strong></div>
                      )}
                      <div>Total Items: <strong>{diagnosisResult.summary?.totalItems || 0}</strong></div>
                    </div>
                  </div>

                  <div className="diagnosis-section">
                    <div className="diagnosis-title">By Vertical</div>
                    <div className="diagnosis-items">
                      {diagnosisResult.verticals && Object.entries(diagnosisResult.verticals).map(([name, data]) => (
                        <div key={name} className="vertical-status">
                          <span>{name}:</span>
                          <span>
                            {data.summary?.valid || 0}/{data.summary?.total || 0} valid
                            {data.summary?.broken > 0 && (
                              <span className="text-error"> ({data.summary.broken} broken)</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {diagnosisResult.recommendations?.length > 0 && (
                  <div className="diagnosis-recommendations">
                    <div className="diagnosis-title">Recommendations:</div>
                    <ul>
                      {diagnosisResult.recommendations.map((rec, i) => (
                        <li key={i}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h2>Quick Actions</h2>
            <div className="actions-grid">
              <button
                className="action-button action-data"
                onClick={() => navigate('/admin/training/data')}
                title="Upload and manage training datasets"
              >
                <span className="action-icon">📁</span>
                <span className="action-text">Training Data</span>
              </button>

              <button
                className="action-button action-submit"
                onClick={() => navigate('/admin/training/submit')}
              >
                <span className="action-icon">➕</span>
                <span className="action-text">Submit Training Job</span>
              </button>

              <button
                className="action-button action-jobs"
                onClick={() => navigate('/admin/training/jobs')}
              >
                <span className="action-icon">📋</span>
                <span className="action-text">View All Jobs</span>
              </button>

              <button
                className="action-button action-adapters"
                onClick={() => navigate('/admin/training/adapters')}
              >
                <span className="action-icon">🔌</span>
                <span className="action-text">Manage Adapters</span>
              </button>

              <button
                className="action-button action-benchmark"
                onClick={() => navigate('/admin/benchmark')}
                title="Test adapter quality and performance"
              >
                <span className="action-icon">🎯</span>
                <span className="action-text">Benchmark Adapters</span>
              </button>

              <button
                className="action-button action-seeds"
                onClick={() => navigate('/admin/spine/seeds')}
                title="Edit per-vertical seed questions used by the retrain spine"
              >
                <span className="action-icon">🌱</span>
                <span className="action-text">Spine Seeds</span>
              </button>

              {/* S0 Curriculum Gap Engine (pre-engagement analysis) */}
              <button
                className="action-button action-curriculum-gaps"
                onClick={() => navigate('/admin/curriculum-gaps')}
                title="Curriculum Gap Engine — analyse an institution's syllabus vs pasted JDs"
              >
                <span className="action-icon">📚</span>
                <span className="action-text">Curriculum Gaps</span>
              </button>
            
              {/* ════════════════════════════════════════════════════════════ */}
              {/* NEW: User Management Button                                   */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-users"
                onClick={() => navigate('/admin/users')}
                title="View and manage user accounts"
              >
                <span className="action-icon">👥</span>
                <span className="action-text">User Management</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Security Events (audit browser) — audit.view                   */}
              {/* ════════════════════════════════════════════════════════════ */}
              {user?.permissions?.includes('audit.view') && (
                <button
                  className="action-button action-security"
                  onClick={() => navigate('/admin/security-events')}
                  title="Browse logins, lockouts, password resets, alerts"
                >
                  <span className="action-icon">🛡️</span>
                  <span className="action-text">Security Events</span>
                </button>
              )}

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Smart Invite catalog — offerings + insights (audit.view)       */}
              {/* Same visibility rule and placement as Security Events above.   */}
              {/* ════════════════════════════════════════════════════════════ */}
              {user?.permissions?.includes('audit.view') && (
                <button
                  className="action-button action-connect-catalog"
                  onClick={() => navigate('/admin/connect-catalog')}
                  title="Edit Smart Invite offerings + insight cards"
                >
                  <span className="action-icon">✉️</span>
                  <span className="action-text">Connect Catalog</span>
                </button>
              )}

              {/* ════════════════════════════════════════════════════════════ */}
              {/* R14: Connect Leads -- Early Access spam containment page.    */}
              {/* Same audit.view gate as Catalog; backend adds authenticate   */}
              {/* Admin on /api/admin/connect/leads so super-admin wins.       */}
              {/* ════════════════════════════════════════════════════════════ */}
              {user?.permissions?.includes('audit.view') && (
                <button
                  className="action-button action-connect-leads"
                  onClick={() => navigate('/admin/connect-leads')}
                  title="Early Access leads -- review suspected spam, mark not spam"
                >
                  <span className="action-icon">📥</span>
                  <span className="action-text">Connect Leads</span>
                </button>
              )}

              {/* ════════════════════════════════════════════════════════════ */}
              {/* NEW: Cloud Services & Data Ingestion Buttons                 */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-cloud"
                onClick={() => navigate('/admin/cloud-services')}
                title="Monitor cloud service costs and usage"
              >
                <span className="action-icon">☁️</span>
                <span className="action-text">Cloud Services</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* NEW: Security Settings Button                                 */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-security"
                onClick={() => navigate('/admin/security')}
                title="Security settings and 2FA"
              >
                <span className="action-icon">🔐</span>
                <span className="action-text">Security</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* System Settings Button                                        */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-settings"
                onClick={() => navigate('/admin/settings')}
                title="System settings and document processing configuration"
              >
                <span className="action-icon">⚙️</span>
                <span className="action-text">Settings</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Evaluation Dashboard Button                                   */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-evaluation"
                onClick={() => navigate('/admin/evaluations')}
                title="AI Response Quality Evaluations"
              >
                <span className="action-icon">📊</span>
                <span className="action-text">Evaluations</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Phase 2B: Adapter Hierarchy Button                            */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-adapters"
                onClick={() => navigate('/admin/adapter-hierarchy')}
                title="Configure adapter stacking and fallback chains"
              >
                <span className="action-icon">🧠</span>
                <span className="action-text">Adapter Hierarchy</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Phase 2E: Prompt Management Button                            */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-prompts"
                onClick={() => navigate('/admin/prompts')}
                title="Manage dynamic prompts for SkillBot and Dashboard"
              >
                <span className="action-icon">📝</span>
                <span className="action-text">Prompt Templates</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Civil Construction: Risk Assessment                           */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-risk"
                onClick={() => navigate('/admin/risk-assessment')}
                title="Tender & BoQ Risk Assessment (Civil Construction)"
              >
                <span className="action-icon">⚠️</span>
                <span className="action-text">Risk Assessment</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* API Credits Management                                        */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-credits"
                onClick={() => navigate('/admin/credits')}
                title="Monitor API credits and usage across providers"
              >
                <span className="action-icon">💳</span>
                <span className="action-text">API Credits</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Wiki Intelligence Dashboard                                     */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-wiki"
                onClick={() => navigate('/admin/wiki')}
                title="Wiki Intelligence — Review auto-generated entries & metrics"
              >
                <span className="action-icon">📚</span>
                <span className="action-text">Wiki Review</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Partnership GTM Dashboard                                       */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-partnerships"
                onClick={() => navigate('/admin/partnerships')}
                title="Partnership Pipeline — GTM outreach, tracking & metrics"
              >
                <span className="action-icon">🤝</span>
                <span className="action-text">Partnerships</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Research Studio                                               */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-research"
                onClick={() => navigate('/admin/research')}
                title="Research Studio — Import and curate knowledge"
              >
                <span className="action-icon">🔬</span>
                <span className="action-text">Research Studio</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* SEAL R&D Hub                                                   */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-seal"
                onClick={() => navigate('/admin/seal/dashboard')}
                title="SEAL R&D Hub — AI Quality Monitoring & Self-Evolving Prompts"
              >
                <span className="action-icon">🔬</span>
                <span className="action-text">SEAL R&D Hub</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* SEAL Configuration                                            */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-seal-config"
                onClick={() => navigate('/admin/seal/config')}
                title="SEAL Configuration — System settings and per-domain flags"
              >
                <span className="action-icon">⚙️</span>
                <span className="action-text">SEAL Config</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* SEAL Candidates                                                */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-seal-candidates"
                onClick={() => navigate('/admin/seal/candidates')}
                title="SEAL Candidates — Review AI-generated improvement suggestions"
              >
                <span className="action-icon">📋</span>
                <span className="action-text">SEAL Candidates</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* SkillForge Review — approve teacher answers (publishes to Wiki) */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-skillforge-review"
                onClick={() => navigate('/admin/skillforge/review')}
                title="SkillForge Review — approve teacher answers (publishes to Wiki)"
              >
                <span className="action-icon">🎓</span>
                <span className="action-text">SkillForge Review</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Persona Agents (Phase 1 scaffold — backend 501 until V2)      */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-persona-agents"
                onClick={() => navigate('/admin/persona-agents')}
                title="Persona Agents — V2 persona-agent-service admin (scaffold)"
              >
                <span className="action-icon">🤖</span>
                <span className="action-text">Persona Agents</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Workflow Catalog (WF-4) — browse + run V2 workflows           */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-workflows"
                onClick={() => navigate('/admin/workflows')}
                title="Workflow Catalog — browse, start, watch, and approve Workflow Factory runs"
              >
                <span className="action-icon">⚙️</span>
                <span className="action-text">Workflow Catalog</span>
              </button>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Run Agent — invoke SkillAgent manifest via V1 admin proxy     */}
              {/* ════════════════════════════════════════════════════════════ */}
              <button
                className="action-button action-run-agent"
                onClick={() => navigate('/admin/agents/run')}
                title="Run Agent — invoke a SkillAgent manifest and inspect retrieval + grounding"
              >
                <span className="action-icon">▶️</span>
                <span className="action-text">Run Agent</span>
              </button>

            </div>
          </div>

          {/* Recent Training Jobs */}
          <div className="recent-jobs">
            <div className="section-header">
              <h2>Recent Training Jobs</h2>
              <button
                className="view-all-link"
                onClick={() => navigate('/admin/training/jobs')}
              >
                View All →
              </button>
            </div>

            {recentJobs.length === 0 ? (
              <div className="empty-state">
                <p>No training jobs yet</p>
                <button
                  className="primary-button"
                  onClick={() => navigate('/admin/training/submit')}
                >
                  Submit Your First Job
                </button>
              </div>
            ) : (
              <div className="jobs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Domain</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Submitted By</th>
                      <th>Duration</th>
                      <th>Progress</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentJobs.map(job => (
                      <tr key={job._id}>
                        <td className="job-id">
                          <code>{job.jobId || job._id.slice(-8)}</code>
                        </td>
                        <td className="job-domain">{job.domain}</td>
                        <td>
                          <span className={`status-badge ${getStatusClass(job.status)}`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="job-date">{formatDate(job.createdAt)}</td>
                        <td className="job-user">{job.createdBy?.fullName || job.createdBy?.username || 'Unknown'}</td>
                        <td className="job-duration">{formatDuration(job.createdAt, job.completedAt, job.status)}</td>
                        <td className="job-progress">
                          {(job.status === 'training' || job.status === 'uploading_dataset' || job.status === 'submitting_training') && job.progress !== undefined ? (
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${job.progress || 0}%` }}
                              ></div>
                              <span className="progress-text">
                                {job.progress || 0}%
                              </span>
                            </div>
                          ) : job.status === 'completed' ? (
                            <span>✓ 100%</span>
                          ) : (
                            <span>--</span>
                          )}
                        </td>
                        <td className="job-actions">
                          <button
                            className="action-link"
                            onClick={() => navigate(`/admin/training/jobs/${job.jobId}`)}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Active Adapters */}
          <div className="active-adapters">
            <div className="section-header">
              <h2>Active Adapters</h2>
              <button
                className="view-all-link"
                onClick={() => navigate('/admin/training/adapters')}
              >
                Manage →
              </button>
            </div>

            {adapters.filter(a => a.active).length === 0 ? (
              <div className="empty-state">
                <p>No active adapters</p>
              </div>
            ) : (
              <div className="adapters-grid">
                {adapters.filter(a => a.active).slice(0, 4).map(adapter => (
                  <div key={adapter._id} className="adapter-card">
                    <div className="adapter-header">
                      <span className="adapter-domain">{adapter.domain}</span>
                      {adapter.subDomain && (
                        <span className="adapter-subdomain"> / {adapter.subDomain}</span>
                      )}
                      <span className="adapter-status-dot"></span>
                    </div>
                    <div className="adapter-info">
                      <div className="adapter-version">v{adapter.version}</div>
                      <div className="adapter-metrics">
                        {adapter.quality?.testAccuracy
                          ? `Accuracy: ${(adapter.quality.testAccuracy * 100).toFixed(1)}%`
                          : adapter.status === 'ready' || adapter.status === 'deployed'
                          ? 'Ready for use'
                          : 'No metrics available'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* System Health Modal */}
        {showHealthModal && (
          <div className="modal-overlay" onClick={() => setShowHealthModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>System Health</h2>
                <button className="modal-close" onClick={() => setShowHealthModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="health-detail">
                  <span className="detail-label">Status:</span>
                  <span className={`detail-value ${systemHealth?.status === 'healthy' ? 'status-healthy' : 'status-unhealthy'}`}>
                    {systemHealth?.status === 'healthy' ? '✓ Healthy' : '✗ Unhealthy'}
                  </span>
                </div>
                <div className="health-detail">
                  <span className="detail-label">MongoDB:</span>
                  <span className="detail-value">Connected</span>
                </div>
                <div className="health-detail">
                  <span className="detail-label">Redis:</span>
                  <span className="detail-value">Connected</span>
                </div>
                <div className="health-detail">
                  <span className="detail-label">Training Service:</span>
                  <span className="detail-value">Running</span>
                </div>
                <div className="health-detail">
                  <span className="detail-label">Uptime:</span>
                  <span className="detail-value">{systemHealth?.uptime || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
